/** Customers and leads (C02-001..004): Customer 360, audit buyers as leads, CSV import with dry run, lead SLA. */
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Icon } from '../../../shell/Icon';
import { Button, Heading, Panel, Text } from '@qm/ui';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, DataTable, ErrorNote, Field, Loadable, Money, Notice, Screen, Stat, Tabs, When } from '../kit/ui';
import type { ImportView, OrderView } from '../api';
import { CUSTOMER_TABS, DRAWER_TABS, customerTab, deliverableCount, drawerTab, filterAvailability, filterCustomers, healthOptions, latestReport, ownerOptions, planOptions, serviceOptions, whatsappOptions, whatsappState, type FilterKey } from '../lib/customers-logic';
import { avatarTone, initialsOf } from '../lib/home-logic';
import { IMPORT_TARGETS, ORDER_TONE, autoMapping, csvHeader, displayAmountMinor, leadSla, mappingValid, orderLabelKey } from '../lib/logic';
import { useAdminApi } from './layout';

const NA = <span className="qa-na" aria-label="Not connected yet" data-na="true">-</span>;

export function CustomersPage() {
  const { t } = useSurface();
  const api = useAdminApi();
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') ?? '';
  const plan = sp.get('plan') ?? '';
  const health = sp.get('health') ?? '';
  const owner = sp.get('owner') ?? '';
  const service = sp.get('service') ?? '';
  const wa = sp.get('wa') ?? '';
  const tab = customerTab(sp.get('tab'));
  const sel = sp.get('sel') ?? '';
  const list = useResource(`customers:${q}`, () => api.customers(q));
  const patch = (o: Record<string, string | null>) => { const n = new URLSearchParams(sp); for (const [k, v] of Object.entries(o)) { if (v) n.set(k, v); else n.delete(k); } setSp(n); };
  return (
    <Screen title={t('portal.admin.customers.title')} testId="customers">
      <Tabs label={t('portal.admin.customers.title')} active={tab} onSelect={(v) => patch({ tab: v === 'active' ? null : v, sel: null })}
        tabs={CUSTOMER_TABS.map((id) => ({ id, label: t(`portal.admin.customers.tab.${id}`) }))} />
      <Loadable resource={list}>
        {(all) => {
          const avail = filterAvailability(all);
          const tabReady = tab === 'active' || avail.status;
          const rows = tabReady ? filterCustomers(all, { plan, health, owner, service, whatsapp: wa, tab }) : [];
          const plans = planOptions(all);
          const needs = (k: FilterKey) => (avail[k] ? undefined : t('portal.admin.customers.needs', { field: t(`portal.admin.customers.field.${k}`) }));
          return (
            <div className="qa-cust" data-open={sel ? 'true' : 'false'}>
              <div className="qa-cust__main">
                <form className="qs-filters" role="search" onSubmit={(e) => { e.preventDefault(); const v = String(new FormData(e.currentTarget).get('q') ?? ''); patch({ q: v || null, sel: null }); }}>
                  <Field label={t('portal.admin.search')}>{(id) => <input id={id} name="q" key={q} type="search" defaultValue={q} />}</Field>
                  <Field label={t('portal.admin.customers.filter.plan')}>{(id) => (
                    <select id={id} value={plan} onChange={(e) => patch({ plan: e.target.value || null })}>
                      <option value="">{t('portal.admin.any')}</option>
                      {plans.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  )}</Field>
                  <Field label={t('portal.admin.customers.filter.health')} hint={needs('health')}>{(id, d) => (
                    <select id={id} value={health} disabled={!avail.health} aria-describedby={d} onChange={(e) => patch({ health: e.target.value || null })}>
                      <option value="">{t('portal.admin.any')}</option>
                      {healthOptions(all).map((b) => <option key={b} value={b}>{t(`portal.admin.customers.health.${b}`)}</option>)}
                    </select>
                  )}</Field>
                  <Field label={t('portal.admin.customers.filter.owner')} hint={needs('owner')}>{(id, d) => (
                    <select id={id} value={owner} disabled={!avail.owner} aria-describedby={d} onChange={(e) => patch({ owner: e.target.value || null })}>
                      <option value="">{t('portal.admin.any')}</option>
                      {ownerOptions(all).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  )}</Field>
                  <Field label={t('portal.admin.customers.filter.service')} hint={needs('service')}>{(id, d) => (
                    <select id={id} value={service} disabled={!avail.service} aria-describedby={d} onChange={(e) => patch({ service: e.target.value || null })}>
                      <option value="">{t('portal.admin.any')}</option>
                      {serviceOptions(all).map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  )}</Field>
                  <Field label={t('portal.admin.customers.filter.whatsapp')} hint={needs('whatsapp')}>{(id, d) => (
                    <select id={id} value={wa} disabled={!avail.whatsapp} aria-describedby={d} onChange={(e) => patch({ wa: e.target.value || null })}>
                      <option value="">{t('portal.admin.any')}</option>
                      {whatsappOptions(all).map((x) => <option key={x} value={x}>{t(`portal.admin.customers.wa.${x}`)}</option>)}
                    </select>
                  )}</Field>
                  <Button type="button" onClick={() => setSp({})} data-testid="customers-clear">{t('portal.admin.customers.filter.clear')}</Button>
                </form>
                <p className="qa-note">{t('portal.admin.customers.columnsNote')}</p>
                {!tabReady ? <Notice>{t('portal.admin.customers.needs', { field: t('portal.admin.customers.field.status') })}</Notice> : null}
                {tabReady ? (
                  <DataTable rows={rows} rowKey={(c) => c.id} empty={t('portal.kit.empty')} rowClassName={(c) => (c.id === sel ? 'qs-selected' : undefined)} columns={[
                    { key: 'n', header: t('portal.admin.customers.col.customer'), render: (c) => (
                      <span className="qa-who">
                        <span className="qs-avatar" data-tone={avatarTone(c.name)} aria-hidden="true">{initialsOf(c.name)}</span>
                        <span className="qa-who__copy">
                          <button type="button" className="qa-pick" aria-pressed={c.id === sel} aria-label={t('portal.admin.customers.select', { name: c.name })} onClick={() => patch({ sel: c.id === sel ? null : c.id, dtab: null })}>{c.name}</button>
                          <small>{c.email ?? ''}</small>
                        </span>
                      </span>
                    ) },
                    { key: 'p', header: t('portal.admin.customers.plan'), render: (c) => c.planCode ?? '-' },
                    { key: 'h', header: t('portal.admin.customers.col.health'), narrow: 'hide', render: () => NA },
                    { key: 'l', header: t('portal.admin.customers.col.leads'), narrow: 'hide', render: () => NA },
                    { key: 'd', header: t('portal.admin.customers.col.deliverables'), narrow: 'hide', render: () => NA },
                    { key: 'r', header: t('portal.admin.customers.col.nextReport'), narrow: 'hide', render: () => NA },
                    { key: 'w', header: t('portal.admin.customers.col.whatsapp'), narrow: 'hide', render: () => NA },
                    { key: 'a', header: t('portal.admin.customers.col.activity'), narrow: 'hide', render: () => NA },
                  ]} />
                ) : null}
              </div>
              {sel ? <CustomerDrawer id={sel} onClose={() => patch({ sel: null, dtab: null })} /> : null}
            </div>
          );
        }}
      </Loadable>
    </Screen>
  );
}

/** Right-hand account drawer: real plan, deliverables, report and WhatsApp state from Customer 360; the rest is labelled not connected. */
export function CustomerDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const { t } = useSurface();
  const api = useAdminApi();
  const [sp, setSp] = useSearchParams();
  const tab = drawerTab(sp.get('dtab'));
  const c = useResource(`customer:${id}`, () => api.customer(id));
  const setTab = (v: string) => { const n = new URLSearchParams(sp); n.set('dtab', v); setSp(n); };
  const nc = <span className="qa-sample">{t('portal.admin.customers.notConnected')}</span>;
  return (
    <aside className="qs-aside" aria-label={t('portal.admin.customers.drawer.tabs')} data-testid="customer-drawer">
      <Loadable resource={c}>
        {(v) => {
          const del = deliverableCount(v.panels);
          const report = latestReport(v.panels.reports);
          const wa = whatsappState(v.panels.whatsapp.status);
          return (
            <>
              <div className="qs-aside__head">
                <div>
                  <Heading level={2}>{t('portal.admin.customers.drawer.title', { name: v.name })}</Heading>
                  <span className="qa-shield"><Icon name="shield" size={14} />{t('portal.admin.customers.drawer.isolated')}</span>
                </div>
                <Button className="qa-drawer__x" onClick={onClose} aria-label={t('portal.admin.customers.drawer.close')}>{t('portal.kit.close')}</Button>
              </div>
              <Tabs label={t('portal.admin.customers.drawer.tabs')} active={tab} onSelect={setTab} tabs={DRAWER_TABS.map((d) => ({ id: d, label: t(`portal.admin.customers.drawer.tab.${d}`) }))}>
                {tab === 'summary' ? (
                  <ul className="qs-kv" data-testid="drawer-summary">
                    <li><span className="qs-kv__label">{t('portal.admin.customers.drawer.auditScore')}</span><span className="qs-kv__value">{nc}</span></li>
                    <li><span className="qs-kv__label">{t('portal.admin.customers.drawer.qualifiedLeads')}</span><span className="qs-kv__value">{nc}</span></li>
                    <li><span className="qs-kv__label">{t('portal.admin.customers.drawer.deliverables')}</span><span className="qs-kv__value" data-testid="drawer-deliverables">{del.total === 0 ? '-' : `${del.done}/${del.total}`}</span></li>
                    <li><span className="qs-kv__label">{t('portal.admin.customers.drawer.report')}</span><span className="qs-kv__value" data-testid="drawer-report">{report ? `${report.period} · ${report.status}` : t('portal.admin.customers.drawer.noReport')}</span></li>
                    <li><span className="qs-kv__label">{t('portal.admin.customers.drawer.whatsapp')}</span><span className="qs-kv__value"><span className="qs-status"><span className="qs-dot" data-tone={wa === 'connected' ? 'good' : wa === 'action' ? 'warn' : undefined} aria-hidden="true" />{t(`portal.admin.customers.wa.${wa}`)}</span></span></li>
                    <li><span className="qs-kv__label">{t('portal.admin.customers.drawer.scope')}</span><span className="qs-kv__value">{t('portal.admin.customers.drawer.scopeValue')}</span></li>
                  </ul>
                ) : tab === 'people' ? (
                  <ul className="qs-kv"><li><span className="qs-kv__label">{t('portal.admin.customers.email')}</span><span className="qs-kv__value">{v.email ?? '-'}</span></li><li><span className="qs-kv__label">{t('portal.admin.customers.drawer.owner')}</span><span className="qs-kv__value">{v.ownerId ?? t('portal.admin.customers.drawer.unassigned')}</span></li></ul>
                ) : tab === 'services' ? (
                  v.panels.entitlements.length === 0 ? <p className="qa-note">{t('portal.admin.customers.drawer.noServices')}</p>
                    : <ul className="qs-kv">{v.panels.entitlements.map((e) => <li key={e.key}><span className="qs-kv__label">{e.key}</span><span className="qs-kv__value">{e.status}</span></li>)}</ul>
                ) : tab === 'whatsapp' ? (
                  <ul className="qs-kv"><li><span className="qs-kv__label">{t('portal.admin.customers.drawer.whatsapp')}</span><span className="qs-kv__value">{t(`portal.admin.customers.wa.${wa}`)}{v.panels.whatsapp.label ? ` · ${v.panels.whatsapp.label}` : ''}</span></li></ul>
                ) : <p>{nc}</p>}
              </Tabs>
              <div className="qa-drawer__actions">
                <Link className="qm-button" data-variant="accent" to={`/customers/${encodeURIComponent(v.id)}`} data-testid="drawer-open">{t('portal.admin.customers.drawer.open')}</Link>
                {(['invite', 'manage', 'audit'] as const).map((k) => <Button key={k} disabled title={t('portal.admin.customers.notConnected')}>{t(`portal.admin.customers.drawer.${k}`)}</Button>)}
              </div>
            </>
          );
        }}
      </Loadable>
    </aside>
  );
}

/** Complimentary orders render as "Complimentary" with no income amount; never "Paid" (C02-001). */
export function OrderBadge({ order }: { order: Pick<OrderView, 'paymentKind'> }) {
  const { t } = useSurface();
  return <Badge tone={ORDER_TONE[order.paymentKind]} testId={`order-${order.paymentKind}`}>{t(orderLabelKey(order))}</Badge>;
}

export function Customer360Page() {
  const { t } = useSurface();
  const api = useAdminApi();
  const { id = '' } = useParams();
  const c = useResource(`customer:${id}`, () => api.customer(id));
  return (
    <Loadable resource={c}>
      {(v) => (
        <Screen title={v.name} subtitle={v.email ?? undefined} testId="customer-360">
          <div className="qx-grid">
            <Stat label={t('portal.admin.customers.plan')} value={v.panels.plan.code ?? '-'} />
            <Stat label={t('portal.admin.customers.deliverables')} value={v.panels.deliverables.averageProgressPct === null ? '-' : `${v.panels.deliverables.averageProgressPct}%`} />
            <Stat label={t('portal.admin.customers.whatsapp')} value={v.panels.whatsapp.label ?? v.panels.whatsapp.status} />
          </div>
          <Panel>
            <Heading level={2}>{t('portal.admin.customers.entitlements')}</Heading>
            <DataTable rows={v.panels.entitlements} rowKey={(e) => e.key} empty={t('portal.kit.empty')} columns={[
              { key: 'k', header: t('portal.admin.customers.entitlement'), render: (e) => e.key },
              { key: 's', header: t('portal.admin.status'), render: (e) => e.status },
            ]} />
          </Panel>
          <Panel>
            <Heading level={2}>{t('portal.admin.customers.orders')}</Heading>
            <DataTable rows={v.panels.orders} rowKey={(o) => o.orderRef} empty={t('portal.kit.empty')} columns={[
              { key: 'r', header: t('portal.admin.customers.order'), render: (o) => o.orderRef },
              { key: 'p', header: t('portal.admin.customers.product'), render: (o) => o.product },
              { key: 'k', header: t('portal.admin.customers.payment'), render: (o) => <OrderBadge order={o} /> },
              { key: 'a', header: t('portal.admin.customers.amount'), render: (o) => <Money minor={displayAmountMinor(o)} currency={o.currency} /> },
              { key: 'd', header: t('portal.admin.customers.date'), render: (o) => <When iso={o.orderedAt} /> },
            ]} />
          </Panel>
          <Panel>
            <Heading level={2}>{t('portal.admin.customers.reports')}</Heading>
            <DataTable rows={v.panels.reports} rowKey={(r) => r.period} empty={t('portal.kit.empty')} columns={[
              { key: 'p', header: t('portal.admin.customers.period'), render: (r) => r.period },
              { key: 's', header: t('portal.admin.status'), render: (r) => r.status },
            ]} />
          </Panel>
        </Screen>
      )}
    </Loadable>
  );
}

export function LeadsPage() {
  const { t, now } = useSurface();
  const api = useAdminApi();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') ?? 'all';
  const list = useResource(`leads:${tab}`, () => api.leads(tab === 'breached' ? { sla: 'breached' } : tab === 'buyers' ? { status: 'buyer' } : {}));
  const contacted = useAction(api.markContacted);
  return (
    <Screen title={t('portal.admin.leads.title')} testId="leads">
      <Tabs label={t('portal.admin.leads.title')} active={tab} onSelect={(v) => setSp({ tab: v })} tabs={[
        { id: 'all', label: t('portal.admin.leads.tab.all') }, { id: 'buyers', label: t('portal.admin.leads.tab.buyers') }, { id: 'breached', label: t('portal.admin.leads.tab.breached') },
      ]} />
      <Link to="/leads/import">{t('portal.admin.import.title')}</Link>
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(l) => l.id} empty={t('portal.kit.empty')} columns={[
          { key: 'n', header: t('portal.admin.customers.name'), render: (l) => l.name },
          { key: 's', header: t('portal.admin.leads.source'), render: (l) => l.source ?? '-' },
          { key: 'o', header: t('portal.admin.leads.orderStatus'), render: (l) => (l.orderStatus ? <Badge testId={`lead-order-${l.orderStatus}`}>{t(`portal.admin.leads.order.${l.orderStatus}`)}</Badge> : '-') },
          { key: 'sla', header: t('portal.admin.leads.sla'), render: (l) => { const s = leadSla(l, now()); return <Badge tone={s === 'breached' ? 'bad' : s === 'due_soon' ? 'warn' : s === 'contacted' ? 'good' : 'neutral'} testId={`sla-${s}`}>{t(`portal.admin.leads.slaState.${s}`)}</Badge>; } },
          { key: 'due', header: t('portal.admin.leads.slaDue'), narrow: 'hide', render: (l) => <When iso={l.slaDueAt ?? null} /> },
          { key: 'a', header: '', render: (l) => (!l.contactedAt ? <Button onClick={async () => { if (await contacted.run(l.id)) list.reload(); }}>{t('portal.admin.leads.markContacted')}</Button> : null) },
        ]} />}
      </Loadable>
      <Text tone="muted">{t('portal.admin.leads.slaNote')}</Text>
      <ErrorNote error={contacted.error} />
    </Screen>
  );
}

/** CSV import: map fields, dry run (creates/updates/duplicates/rejected), then commit; reversible (C02-003). */
export function ImportPage() {
  const { t } = useSurface();
  const api = useAdminApi();
  const [csv, setCsv] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [run, setRun] = useState<ImportView | null>(null);
  const dry = useAction(api.startImport);
  const commit = useAction(api.commitImport);
  const revert = useAction(api.revertImport);
  const headers = csvHeader(csv);
  const onFile = async (f: File | undefined) => { if (!f) return; const text = await f.text(); setCsv(text); setMapping(autoMapping(csvHeader(text))); };
  return (
    <Screen title={t('portal.admin.import.title')} subtitle={t('portal.admin.import.subtitle')} testId="import">
      <Field label={t('portal.admin.import.file')}>{(id) => <input id={id} type="file" accept=".csv,text/csv" onChange={(e) => void onFile(e.target.files?.[0])} />}</Field>
      {headers.length ? (
        <Panel data-testid="mapping">
          <Heading level={2}>{t('portal.admin.import.mapping')}</Heading>
          {headers.map((h) => (
            <Field key={h} label={h}>{(id) => <select id={id} value={mapping[h] ?? ''} onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}><option value="">{t('portal.admin.import.skip')}</option>{IMPORT_TARGETS.map((x) => <option key={x} value={x}>{t(`portal.admin.crm.field.${x}`)}</option>)}</select>}</Field>
          ))}
          {!mappingValid(mapping) ? <Text tone="muted">{t('portal.admin.import.needContact')}</Text> : null}
          <Button variant="primary" disabled={!mappingValid(mapping) || dry.pending} onClick={async () => { const r = await dry.run({ entity: 'contacts', csv, mapping }); if (r) setRun(r); }}>{t('portal.admin.import.dryRun')}</Button>
        </Panel>
      ) : null}
      <ErrorNote error={dry.error ?? commit.error ?? revert.error} />
      {run ? (
        <Panel data-testid="dry-run">
          <div className="qx-grid">
            <Stat label={t('portal.admin.import.rows')} value={run.totals.rows} />
            <Stat testId="creates" label={t('portal.admin.import.creates')} value={run.totals.creates} />
            <Stat testId="updates" label={t('portal.admin.import.updates')} value={run.totals.updates} />
            <Stat testId="duplicates" label={t('portal.admin.import.duplicates')} value={run.totals.duplicates} />
            <Stat testId="rejected" label={t('portal.admin.import.rejected')} value={run.totals.rejected} />
          </div>
          {run.totals.rejected > 0 ? <a href={api.importErrorsUrl(run.id)} download data-testid="errors-file">{t('portal.admin.import.errorsFile')}</a> : null}
          {run.status === 'dry_run' ? <Button variant="primary" disabled={commit.pending} onClick={async () => { const r = await commit.run(run.id); if (r) setRun(r); }}>{t('portal.admin.import.commit')}</Button> : null}
          {run.status === 'committed' ? <Button onClick={async () => { const r = await revert.run(run.id); if (r) setRun(r); }}>{t('portal.admin.import.revert')}</Button> : null}
          <Notice>{t(`portal.admin.import.status.${run.status}`)}</Notice>
        </Panel>
      ) : null}
    </Screen>
  );
}
